import { isCompanyProperty, type AssetOwnership } from './enums';

/**
 * Whether recording this asset's return has to include the login on the device.
 *
 * Two conditions, and the second one is the important one.
 *
 * 1. **The category says so.** Laptops and desktops have a local account worth
 *    collecting; a chair does not. Per category rather than per asset because it is a
 *    fact about the KIND of thing, and an answer asked 400 times is one answered
 *    carelessly. Requiring it everywhere instead would put a password box in front of
 *    recording a returned monitor, and the first person who cannot fill it in stops
 *    recording returns.
 *
 * 2. **The company owns it.** An employee's own laptop is registered and issued here
 *    like anything else, because the register has to answer "what is this person
 *    using" - but the company has no basis whatsoever to demand the password to a
 *    device it does not own. That is the same line `isCompanyProperty` already draws
 *    at exit clearance, drawn here for the same reason.
 *
 * The SPA calls this to decide whether to mark the fields required; the API calls it
 * to refuse the request. As always, the API's answer is the authoritative one.
 */
export function requiresDeviceCredential(asset: {
  categoryRequiresCredentials: boolean;
  ownership: AssetOwnership;
}): boolean {
  return asset.categoryRequiresCredentials && isCompanyProperty(asset.ownership);
}

/**
 * Whether a credential may be recorded against this asset at all.
 *
 * Wider than `requiresDeviceCredential`: IT may volunteer the login for a returned
 * tablet whose category does not demand one, and that is useful. What is refused is
 * storing the password to somebody's personal device - not required, not optional,
 * not at all.
 */
export function allowsDeviceCredential(asset: { ownership: AssetOwnership }): boolean {
  return isCompanyProperty(asset.ownership);
}

/** Long enough for a passphrase; bounded so a paste cannot become the record. */
export const MAX_DEVICE_USERNAME_LENGTH = 200;
export const MAX_DEVICE_PASSWORD_LENGTH = 500;

/**
 * How a stored credential reads on screen, without revealing anything.
 *
 * Three states that must not be merged. 'cleared' exists because "IT wiped this
 * machine and deliberately removed the password" and "nobody ever collected one" look
 * identical if you only check for NULL - and one of those is a job done while the
 * other is a job missed.
 */
export function deviceCredentialState(assignment: {
  deviceUsername: string | null | undefined;
  hasSecret: boolean;
  deviceSecretClearedAt: Date | string | null | undefined;
}): 'none' | 'held' | 'cleared' {
  if (assignment.hasSecret) return 'held';
  if (assignment.deviceSecretClearedAt != null || assignment.deviceUsername) return 'cleared';
  return 'none';
}
